import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Trash2 } from 'lucide-react';
import { agentAppAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const fmt = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

export default function AgentLedgerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setHeader } = useOutletContext();
  const { user } = useAuth();
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deletingKey, setDeletingKey] = useState(null);

  const isAdmin = user?.role === 'admin';
  const canDelete = isAdmin || user?.permissions?.canAdjustBalance;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await agentAppAPI.getLedger(id);
      setLedger(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load balance sheet');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setHeader({ title: 'Balance Sheet', showBack: true });
  }, [setHeader]);

  // Every entry type is deletable: a real auto-monthly Bill doc, any Payment (completed or
  // still-pending/unpaid — the backend branches on status to reverse the balance correctly),
  // a Collection Pending visit record (no balance effect, nothing to reverse), or a manual
  // Balance Adjustment (reversed via its ChangeLog entry).
  const canDeleteEntry = (entry) => !!entry.billId || !!entry.paymentId || !!entry.attemptId || !!entry.changeLogId;

  const handleDelete = async (e, entry) => {
    e.stopPropagation();
    const label = entry.billId ? 'bill'
      : entry.attemptId ? 'Collection Pending record'
      : entry.changeLogId ? 'balance adjustment'
      : 'payment';
    if (!window.confirm(`Delete this ${label}? This will reverse its effect on the balance.`)) return;

    const key = entry.billId || entry.paymentId || entry.attemptId || entry.changeLogId;
    setDeletingKey(key);
    try {
      if (entry.billId) {
        await agentAppAPI.deleteLedgerBill(id, entry.billId);
      } else if (entry.attemptId) {
        await agentAppAPI.deleteLedgerAttempt(id, entry.attemptId);
      } else if (entry.changeLogId) {
        await agentAppAPI.deleteLedgerAdjustment(id, entry.changeLogId);
      } else {
        await agentAppAPI.deleteLedgerPayment(id, entry.paymentId);
      }
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    } finally {
      setDeletingKey(null);
    }
  };

  if (loading || !ledger) {
    return <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading…</div>;
  }

  return (
    <div style={{ padding: 12 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <Stat label="Total Billed" value={ledger.totalBilled} />
        <Stat label="Total Paid" value={ledger.totalPaid} color="#16a34a" />
        <Stat label="Current Due" value={ledger.outstandingBalance} color={ledger.outstandingBalance > 0 ? '#dc2626' : '#16a34a'} />
      </div>

      {ledger.entries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: '.85rem' }}>
          No bills or payments recorded yet
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {ledger.entries.map((e) => {
            const key = e.billId || e.paymentId || e.attemptId || e.changeLogId || `${e.type}-${e.date}`;
            const clickable = !!e.paymentId;
            const isPendingVisit = e.type === 'pending_visit';
            const isUnpaidBill = e.type === 'bill' && e.status === 'pending';
            return (
              <div
                key={key}
                onClick={clickable ? () => navigate(`/agent/receipt/${e.paymentId}`) : undefined}
                style={{
                  background: 'var(--bg-card)', border: '1px solid var(--border)',
                  borderLeft: isPendingVisit ? '3px solid #f59e0b' : '1px solid var(--border)',
                  borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10,
                  cursor: clickable ? 'pointer' : 'default',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '.88rem', color: isPendingVisit ? '#b45309' : 'inherit' }}>
                    {e.description}
                    {isUnpaidBill && (
                      <span style={{ marginLeft: 6, fontSize: '.65rem', fontWeight: 700, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '1px 6px' }}>
                        UNPAID
                      </span>
                    )}
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '.74rem' }}>
                    {new Date(e.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}, {new Date(e.date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}{e.reference ? ` · ${e.reference}` : ''}
                  </div>
                  {e.periodFrom && e.periodTo && (
                    <div style={{ color: 'var(--text-muted)', fontSize: '.72rem' }}>
                      Validity: {new Date(e.periodFrom).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} to {new Date(e.periodTo).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} ({Math.round((new Date(e.periodTo) - new Date(e.periodFrom)) / 86400000) + 1} days){e.periodEstimated ? ' (approx.)' : ''}
                    </div>
                  )}
                  {e.oldAmount > 0 && (
                    <div style={{ color: '#b45309', fontSize: '.72rem', fontWeight: 600 }}>
                      Old amount ₹{fmt(e.oldAmount)} + Current ₹{fmt(e.debit)} = Total ₹{fmt(e.oldAmount + e.debit)}
                    </div>
                  )}
                </div>
                {!isPendingVisit && (
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    {e.debit > 0 && <div style={{ color: isUnpaidBill ? '#b45309' : '#dc2626', fontWeight: 700, fontSize: '.85rem' }}>-₹{fmt(e.debit)}</div>}
                    {e.credit > 0 && <div style={{ color: '#16a34a', fontWeight: 700, fontSize: '.85rem' }}>+₹{fmt(e.credit)}</div>}
                    <div style={{ color: 'var(--text-muted)', fontSize: '.7rem' }}>Bal ₹{fmt(e.balance)}</div>
                  </div>
                )}
                {canDelete && canDeleteEntry(e) && (
                  <button
                    onClick={(ev) => handleDelete(ev, e)}
                    disabled={deletingKey === key}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: 6, flexShrink: 0 }}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ flex: 1, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 6px', textAlign: 'center' }}>
      <div style={{ fontSize: '.68rem', color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ fontWeight: 700, fontSize: '.95rem', color: color || 'var(--text)' }}>₹{fmt(value)}</div>
    </div>
  );
}
